import { JOB_CATEGORIES } from 'src/app/shared/constants/jobCategories';
import {
  findAdcentreCategoryIdsBySeekId,
  getClassificationDataByAdCenterIds,
} from 'src/app/utils/classifications';
import { mapTextToRestrictedEmojisText } from 'src/app/utils/emoji';
import { isAggregatedAd } from 'src/app/utils/isAggregatedAd';
import { getErrorMessage } from 'src/app/utils/logging/utils/get-error-message';
import {
  mapSalaryTypeGraphToPayTypeUI,
  getSalaryCurrency,
  minorToMajor,
} from 'src/app/utils/salary/getSalary';
import type {
  DraftJobSalaryCurrency,
  Job,
  SalaryType as SalaryTypeGraph,
} from 'src/graphql/generated';
import { CajaWorkType } from 'src/types/cajaWorkType';
import type { MappedFormData } from 'src/types/draft';
import type { FlowType } from 'src/types/jobFlow';
import type { CountryCode } from 'src/types/taxonomy';

const mapStringToWorkType = (value: string): string => {
  if (value === 'CONTRACT_TEMP') {
    return CajaWorkType.Contract;
  }
  return value.toLowerCase();
};

export interface mapJobToDraftRequest {
  jobData: Job;
  advertiserNation: string;
  hasLinkOutEnabled: boolean;
  flowType: FlowType;
  canUpdateExpiryDate: boolean;
  shouldRestrictEmojis: boolean;
}

export const mapJobToDraftValue = (
  request: mapJobToDraftRequest,
): MappedFormData | string => {
  const {
    jobData,
    advertiserNation,
    hasLinkOutEnabled,
    flowType,
    canUpdateExpiryDate,
    shouldRestrictEmojis,
  } = request;
  try {
    const countryCode = advertiserNation.toLowerCase() as CountryCode;

    const payType = mapSalaryTypeGraphToPayTypeUI(
      jobData.payType as SalaryTypeGraph,
    );

    const salaryCurrency = getSalaryCurrency({
      formDataCurrency: jobData.salaryCurrency ?? null,
      hirerNation: countryCode,
      jobLocationCountry: null,
    }) as DraftJobSalaryCurrency;

    const seekSubCategoryId = jobData.subCategoryId;

    // Convert to Adcentre
    const { categoryId, subCategoryId } = findAdcentreCategoryIdsBySeekId(
      JOB_CATEGORIES,
      Number(seekSubCategoryId),
    );

    // Convert back to SEEK Ids, but this time we have the categoryId
    const classificationData = (Boolean(categoryId) &&
      Boolean(subCategoryId) &&
      getClassificationDataByAdCenterIds(
        Number(categoryId),
        Number(subCategoryId),
      )) || { seekClassificationId: null, seekSubClassificationId: null };

    const maxSalaryValueInDollars = minorToMajor(
      salaryCurrency,
      jobData.maxSalary,
    );
    const minSalaryValueInDollars = minorToMajor(
      salaryCurrency,
      jobData.minSalary,
    );

    const mappedFormData: MappedFormData = {
      categoryId: Number(classificationData.seekClassificationId),
      displayPay: Boolean(jobData.salaryText),
      internalReference: jobData.advertiserJobReference || '',
      jobDescription: jobData.jobDescription || '',
      jobSummary: jobData.jobSummary || '',
      jobLocationId: jobData.locationId || '',
      jobTitle: mapTextToRestrictedEmojisText(
        jobData.title,
        shouldRestrictEmojis,
      ),
      keySellingPoint1: mapTextToRestrictedEmojisText(
        jobData.keySellingPoints?.[0] || '',
        shouldRestrictEmojis,
      ),
      keySellingPoint2: mapTextToRestrictedEmojisText(
        jobData.keySellingPoints?.[1] || '',
        shouldRestrictEmojis,
      ),
      keySellingPoint3: mapTextToRestrictedEmojisText(
        jobData.keySellingPoints?.[2] || '',
        shouldRestrictEmojis,
      ),
      maxSalary: maxSalaryValueInDollars,
      minSalary: minSalaryValueInDollars,
      payType,
      phoneNumber: jobData.phoneNumber ?? '',
      productDisplayName: jobData.productDisplayName || '',
      subCategoryId: Number(classificationData.seekSubClassificationId),
      salaryCurrency,
      salaryText: jobData.salaryText || '',
      videoUrl: jobData.videoUrl || '',
      workType: mapStringToWorkType(jobData.workType || ''),
      workArrangements: jobData.workArrangements || [],
    };

    if (
      jobData.isConfidential !== null &&
      jobData.isConfidential !== undefined
    ) {
      mappedFormData.isConfidential = jobData.isConfidential;
    }

    // When an aggregated job (postChannel is IMPORT) is editing via CAJA,
    // even if the linkout permission is not enabled, the applicationFormUrl value should NOT be removed
    // BACKGROUND: for aggregated jobs, jobs api has logic to
    // bypass the checking of linkout permission.
    // Therefore aggregated ads without linkout permission can be posted with linkout url value
    if (isAggregatedAd(jobData.postChannel) || hasLinkOutEnabled) {
      mappedFormData.applicationFormUrl = jobData.applicationFormUrl || '';
    }

    if (flowType === 'edit') {
      if (canUpdateExpiryDate) {
        mappedFormData.expiryDate = jobData.expiryDate || '';
      }
      mappedFormData.questionnaireUrl = jobData.questionnaireUrl || '';
    }

    return mappedFormData;
  } catch (error) {
    return `mapJobToDraftValue | Error: ${getErrorMessage(error)}`;
  }
};
